// Define the application's config state to get PARTNER_TOKEN (assuming it's in some global config or context)
import { userSession } from '../../state/session';
import { ApiError } from 'utils/errors';
import { EErrorCode } from 'config/errors-codes';
import { user } from 'utils/auth/user';

export type TMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

// Define the type for request options
export type RequestOptions = Omit<RequestInit, 'headers' | 'body'> & {
  headers?: Record<string, string>;
  method?: TMethod;
  body?: unknown;
  origin?: string;
  idToken?: boolean;
};

// Reusable function to determine the API base URL
export const getApiBaseUrl = (): string => {
  if (process.env.API_URL) {
    return process.env.API_URL;
  }
  throw new Error('API_URL is not defined.');
};

// Map to track active requests by their unique identifier (method + URL)
const activeRequests = new Map<string, AbortController>();

// Helper function to create a unique request identifier
const getRequestId = (method: string, url: string): string => {
  return `${method}:${url}`;
};

// Function to cancel a specific request if it exists
const cancelRequest = (requestId: string, reason: string = 'Duplicate request'): boolean => {
  const controller = activeRequests.get(requestId);
  if (controller) {
    controller.abort(reason);
    activeRequests.delete(requestId);
    return true;
  }
  return false;
};

// Reusable function for making API calls with error handling and header setup
export const sendApiRequest = async <T>(
  endpoint: string,
  options: RequestOptions = {},
  auth = true,
  contextToken?: string
): Promise<T | undefined> => {
  const PARTNER_TOKEN = String(userSession().get('partnerToken'));
  const AUTH_TOKEN = user.getAccessToken();
  const ID_TOKEN = user.getIdToken();

  const apiUrl = options.origin ?? getApiBaseUrl();
  const url = `${apiUrl}${endpoint}`;
  const method = options.method || 'GET';

  // Create a unique identifier for this request
  const requestId = getRequestId(method, url);

  // Check if there's already an identical request in progress
  if (activeRequests.has(requestId)) {
    if (import.meta?.env?.MODE === 'development') {
      console.log(`Cancelling previous ${method} request to ${url}`);
    }
    // Cancel the previous request to the same endpoint
    cancelRequest(requestId);
  }

  // Setup headers with PARTNER_TOKEN
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Partner-Token': PARTNER_TOKEN,
    ...(options.headers || {}), // Include any additional headers passed in options
    ...(auth && AUTH_TOKEN ? { Authorization: `Bearer ${AUTH_TOKEN}` } : {}),
    ...(auth && options.idToken && ID_TOKEN ? { 'X-Id-Token': ID_TOKEN } : {}),
    ...(contextToken ? { 'Context-Token': `Bearer ${contextToken}` } : {}),
  };

  // Create an AbortController for this request
  const controller = new AbortController();
  const signal = controller.signal;

  // Register this request
  activeRequests.set(requestId, controller);

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      method,
      body: options.body ? JSON.stringify(options.body) : undefined, // Serialize the body if present
      signal, // Attach the signal for aborting requests
    });

    if (!response.ok) {
      // Error handling: Try to extract a JSON error message from the response
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.error || response.statusText;
      const errorCode: EErrorCode = errorData?.code || EErrorCode.INTERNAL_ERROR;
      throw new ApiError(errorCode, `Error: ${response.status} - ${errorMessage}`, { statusCode: response.status });
    }

    // Parse the JSON response
    const data = await response.json();
    return data as T;
  } catch (error) {
    if (error === 'Duplicate request') {
      console.warn(`Duplicate request aborted: ${method} ${url}`, error);
    } else {
      // Log and rethrow other errors for the UI to handle them
      console.error('API request failed:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(EErrorCode.INTERNAL_ERROR, (error as Error).message);
    }
  } finally {
    // Cleanup: Remove this request from tracking when done
    activeRequests.delete(requestId);
  }
};
